import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import LanguageSelector from './LanguageSelector';
import SuperuserFarmPicker from './SuperuserFarmPicker';
import './Navbar.css';
import { useAuth } from '../context/AuthContext';
import { useFarm } from '../context/FarmContext';
import { useTheme } from '../context/ThemeContext';
import { useSuperuserFarmPickerNavigate } from '../hooks/useSuperuserFarmPickerNavigate';
import { isGlobalRole, isFarmLeadRole, normalizeRole } from '../utils/roles';

const UserMenu = lazy(() => import('./UserMenu'));

function decodeRole(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.role;
  } catch {
    return null;
  }
}

export default function Navbar() {
  const { lang, t } = useLang();
  const { effectiveTheme, cycleTheme } = useTheme();
  const { token, user, realUser, ready, logout, impersonatedRole, impersonateRole, resetImpersonation } = useAuth();
  const { farms, effectiveFarmId, contextOverrideId } = useFarm();
  const onSuperuserFarmChange = useSuperuserFarmPickerNavigate();
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const isRtl = dir === 'rtl';
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 991.98px)');
    const syncChrome = () => {
      const active = Boolean(open && media.matches);
      document.documentElement.classList.toggle('shefa-mobile-nav-drawer-open', active);
      if (active) {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      } else {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }
    };
    syncChrome();
    media.addEventListener('change', syncChrome);
    return () => {
      media.removeEventListener('change', syncChrome);
      document.documentElement.classList.remove('shefa-mobile-nav-drawer-open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [open]);

  const role = normalizeRole(user?.role || decodeRole(token));
  const isRealAdmin = normalizeRole(realUser?.role) === 'admin';
  const logged = ready ? Boolean(token) : null;

  const menu =
    logged === null ? (
      <span className="spinner-border spinner-border-sm" />
    ) : logged ? (
      <Suspense fallback={<span className="spinner-border spinner-border-sm" />}>
        <UserMenu t={t} role={role} onLink={() => setOpen(false)} />
      </Suspense>
    ) : null;

  const navbarColor = effectiveTheme === 'light' ? 'navbar-light' : 'navbar-dark';

  const farmValue = contextOverrideId || user?.farmId || effectiveFarmId || '';
  const showFarmPicker = logged && isGlobalRole(realUser?.role) && farms?.length > 0;

  const navBannerFile =
    process.env.REACT_APP_NAVBAR_BANNER_FILE ||
    process.env.REACT_APP_NAVBAR_TILE_FILE ||
    'shefa-navbar-banner.png';
  const navBannerSrc = `${process.env.PUBLIC_URL || ''}/${navBannerFile}`;
  const homeTo = '/';
  const bannerStyle = { backgroundImage: `url('${navBannerSrc.replace(/'/g, '%27')}')` };
  const publicThemeIcon = effectiveTheme === 'dark' ? 'fa-sun' : 'fa-moon';

  if (logged === false) {
    return (
      <nav className="navbar navbar-expand-lg navbar-dark shefa-navbar shefa-navbar--public" dir={dir}>
        <div className="shefa-navbar-banner-bg" aria-hidden="true" />
        <div className="container shefa-navbar__banner-inner shefa-public-navbar__inner">
          <div className="shefa-public-navbar__brand-group">
            <Link className="shefa-public-navbar__brand" to="/" onClick={() => setOpen(false)}>
              <span className="shefa-public-navbar__logo" aria-hidden="true">
                <i className="fas fa-leaf" />
              </span>
              <span>ShefaGreen</span>
            </Link>
            <Link className="shefa-public-navbar__link d-none d-lg-inline-flex" to="/about.html">
              {t('home.hero.about')}
            </Link>
          </div>

          <button
            className={`navbar-toggler shefa-public-navbar__toggler${open ? ' shefa-public-navbar__toggler--open' : ''}`}
            type="button"
            onClick={() => setOpen(!open)}
            aria-controls="publicNavbarNav"
            aria-expanded={open}
            aria-label={open ? t('close') : t('toggleNavigation')}
          >
            {open ? <i className="fas fa-times" aria-hidden="true" /> : <span className="navbar-toggler-icon" />}
          </button>

          <div className={`collapse navbar-collapse shefa-public-navbar__collapse ${open ? 'show' : ''}`} id="publicNavbarNav">
            <Link className="shefa-public-navbar__link d-lg-none" to="/about.html" onClick={() => setOpen(false)}>
              {t('home.hero.about')}
            </Link>
            <div className="shefa-public-navbar__actions">
              <LanguageSelector variant="compact" />
              <button
                type="button"
                className="shefa-public-navbar__icon-button"
                onClick={cycleTheme}
                aria-label={t('theme.toggleAria')}
                title={t('theme.toggleAria')}
              >
                <i className={`fas ${publicThemeIcon}`} aria-hidden="true" />
              </button>
              <Link className="shefa-public-navbar__login" to="/login" onClick={() => setOpen(false)}>
                {t('login')}
              </Link>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={`navbar navbar-expand-lg ${navbarColor} shefa-navbar shefa-navbar--under-banner ${
        logged ? 'shefa-navbar--app' : 'shefa-navbar--loading'
      }`}
      dir={dir}
    >
      {logged ? (
        <Link
          className="shefa-navbar-banner-bg"
          to={homeTo}
          onClick={() => setOpen(false)}
          aria-label={t('nav.home')}
          style={bannerStyle}
        />
      ) : (
        <div className="shefa-navbar-banner-bg" aria-hidden="true" style={bannerStyle} />
      )}
      <div className="container shefa-navbar__banner-inner">
        <Link className="shefa-app-navbar__brand" to={homeTo} onClick={() => setOpen(false)}>
          <span className="shefa-app-navbar__logo" aria-hidden="true">
            <i className="fas fa-leaf" />
          </span>
          <span>ShefaGreen</span>
        </Link>
        {!open ? (
          <button
            className={`navbar-toggler shefa-app-navbar__toggler ${isRtl ? 'me-auto' : 'ms-auto'}`}
            type="button"
            onClick={() => setOpen(!open)}
            aria-controls="navbarNav"
            aria-expanded={open}
            aria-label={t('toggleNavigation')}
          >
            <span className="navbar-toggler-icon" />
          </button>
        ) : null}
        {open ? <button type="button" className="shefa-app-navbar__drawer-backdrop" aria-label={t('close')} onClick={() => setOpen(false)} /> : null}
        <div className={`collapse navbar-collapse ${open ? 'show' : ''}`} id="navbarNav">
          <div className="shefa-app-navbar__drawer-head">
            <Link className="shefa-app-navbar__brand" to={homeTo} onClick={() => setOpen(false)}>
              <span className="shefa-app-navbar__logo" aria-hidden="true">
                <i className="fas fa-leaf" />
              </span>
              <span>ShefaGreen</span>
            </Link>
            <button
              type="button"
              className="shefa-app-navbar__drawer-close"
              onClick={() => setOpen(false)}
              aria-label={t('close')}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
          <ul
            className={`navbar-nav shefa-app-navbar__primary flex-column flex-lg-row w-100 ${
              isRtl ? 'text-end ms-lg-auto' : 'me-lg-auto'
            }`}
          >
            {menu}
          </ul>
          <div
            className={`d-lg-flex align-items-center mt-3 mt-lg-0 shefa-navbar-tools ${
              isRtl ? 'me-lg-auto' : 'ms-lg-auto'
            }`}
          >
            <LanguageSelector variant="compact" />
            <button
              type="button"
              className="shefa-public-navbar__icon-button shefa-app-navbar__icon-button"
              onClick={cycleTheme}
              aria-label={t('theme.toggleAria')}
              title={t('theme.toggleAria')}
            >
              <i className={`fas ${publicThemeIcon}`} aria-hidden="true" />
            </button>
            {logged === null ? <span className="spinner-border spinner-border-sm" /> : null}
            {logged ? (
              <AccountMenu
                t={t}
                role={role}
                user={user}
                isRealAdmin={isRealAdmin}
                showFarmPicker={showFarmPicker}
                farms={farms}
                farmValue={farmValue}
                onFarmChange={onSuperuserFarmChange}
                logout={logout}
                impersonatedRole={impersonatedRole}
                impersonateRole={impersonateRole}
                resetImpersonation={resetImpersonation}
                onNavigate={() => setOpen(false)}
              />
            ) : (
              <Link className="shefa-public-navbar__login" to="/login" onClick={() => setOpen(false)}>
                {t('login')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

function AccountMenu({
  t,
  role,
  user,
  isRealAdmin,
  showFarmPicker,
  farms,
  farmValue,
  onFarmChange,
  logout,
  impersonatedRole,
  impersonateRole,
  resetImpersonation,
  onNavigate,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const normalizedRole = normalizeRole(role);
  const canManageFarm = isFarmLeadRole(normalizedRole) || isGlobalRole(normalizedRole);
  const showAdminPanel = isGlobalRole(normalizedRole);
  const displayName = user?.name || user?.email || t('nav.profile');

  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const closeAndNavigate = () => {
    setOpen(false);
    onNavigate?.();
  };

  return (
    <div className={`shefa-account-menu ${open ? 'show' : ''}`} ref={ref}>
      <button
        type="button"
        className="shefa-account-menu__toggle"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="shefa-account-menu__avatar" aria-hidden="true">
          <i className="fas fa-user" />
        </span>
        <span className="shefa-account-menu__name text-truncate">{displayName}</span>
        <i className="fas fa-chevron-down shefa-account-menu__chevron" aria-hidden="true" />
      </button>
      {open ? (
        <div className="shefa-account-menu__panel" role="menu">
          <div className="shefa-account-menu__identity">
            <strong className="text-truncate">{displayName}</strong>
            {normalizedRole ? (
              <span>
                {t(
                  `role.${
                    normalizedRole === 'farm_owner'
                      ? 'farmOwner'
                      : normalizedRole === 'farm_manager'
                        ? 'farmManager'
                        : normalizedRole
                  }`
                )}
              </span>
            ) : null}
          </div>

          {showFarmPicker ? (
            <div className="shefa-account-menu__section">
              <span className="shefa-account-menu__section-label">{t('theme.farmMenu')}</span>
              <SuperuserFarmPicker
                farms={farms}
                value={farmValue}
                onChange={(value) => {
                  onFarmChange(value);
                  setOpen(false);
                  onNavigate?.();
                }}
                t={t}
                variant="navbar"
              />
            </div>
          ) : null}

          <div className="shefa-account-menu__section">
            <span className="shefa-account-menu__section-label">{t('settings')}</span>
            <Link className="shefa-account-menu__item" to="/profile" onClick={closeAndNavigate}>
              {t('nav.profile')}
            </Link>
            <Link className="shefa-account-menu__item" to="/cuttings" onClick={closeAndNavigate}>
              {t('nav.cuttings')}
            </Link>
            <Link className="shefa-account-menu__item" to="/bugs" onClick={closeAndNavigate}>
              {t('nav.errors')}
            </Link>
          </div>

          {canManageFarm ? (
            <div className="shefa-account-menu__section">
              <span className="shefa-account-menu__section-label">{t('nav.adminMenu')}</span>
              {isGlobalRole(normalizedRole) ? (
                <>
                  <Link className="shefa-account-menu__item" to="/strains-admin" onClick={closeAndNavigate}>
                    {t('nav.manageStrains')}
                  </Link>
                  <Link className="shefa-account-menu__item" to="/cuttings-admin" onClick={closeAndNavigate}>
                    {t('nav.manageCuttings')}
                  </Link>
                </>
              ) : null}
              <Link className="shefa-account-menu__item" to="/users" onClick={closeAndNavigate}>
                {t('nav.users')}
              </Link>
              {isGlobalRole(normalizedRole) ? (
                <Link className="shefa-account-menu__item" to="/admin/farms" onClick={closeAndNavigate}>
                  {t('nav.manageFarms')}
                </Link>
              ) : null}
              {showAdminPanel ? (
                <>
                  <span className="shefa-account-menu__section-label shefa-account-menu__section-label--nested">
                    {t('nav.engineeringPanel')}
                  </span>
                  <Link className="shefa-account-menu__item" to="/devices" onClick={closeAndNavigate}>
                    {t('nav.devices')}
                  </Link>
                  <Link className="shefa-account-menu__item" to="/devices/wifi-setup" onClick={closeAndNavigate}>
                    {t('nav.wifiBle')}
                  </Link>
                  <Link className="shefa-account-menu__item" to="/admin/unassigned-devices" onClick={closeAndNavigate}>
                    {t('nav.unassignedDevices')}
                  </Link>
                  <Link className="shefa-account-menu__item" to="/admin/controllers" onClick={closeAndNavigate}>
                    {t('nav.allControllers')}
                  </Link>
                </>
              ) : null}
            </div>
          ) : null}

          {isRealAdmin ? (
            <div className="shefa-account-menu__section shefa-account-menu__section--debug">
              <span className="shefa-account-menu__section-label">{t('roleSwitcher.label')}</span>
              <div className={`shefa-role-switcher ${impersonatedRole ? 'shefa-role-switcher--active' : ''}`}>
                <select
                  className="form-select form-select-sm shefa-role-switcher__select"
                  value={normalizedRole || ''}
                  onChange={(event) => impersonateRole(event.target.value)}
                >
                  <option value="admin">{t('role.admin')}</option>
                  <option value="superuser">{t('role.superuser')}</option>
                  <option value="farm_owner">{t('role.farmOwner')}</option>
                  <option value="farm_manager">{t('role.farmManager')}</option>
                  <option value="worker">{t('role.worker')}</option>
                </select>
                <button type="button" className="btn btn-sm shefa-role-switcher__reset" onClick={resetImpersonation}>
                  {t('roleSwitcher.reset')}
                </button>
              </div>
            </div>
          ) : null}

          <button
            type="button"
            className="shefa-account-menu__logout"
            onClick={() => {
              setOpen(false);
              logout();
            }}
          >
            {t('logout')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
